import React, { useEffect, useState } from "react";
import useLogout from "../../../utils/Utils";
import { Controller, useForm } from "react-hook-form";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  PreviewAltCard,
  Row,
} from "../../../components/Component";
import MyButton from "../../../myComponents/form/MyButton";
import Status from "../../../myComponents/form/Status";
import Required from "../../../myComponents/validate/Required";
import { Badge, Form, Label } from "reactstrap";
import Select from "react-select";
import { createSessionAPI, getSessionAPI, updateSessionAPI } from "../../../api/LMS/master";
import { toast } from "react-toastify";
import { monthList } from "../../../assets/constants/constants";
import MyDataTable from "../../../myComponents/DataTable/MyDataTable";
import ConfirmModal from "../../../myComponents/modals/ConfirmModal";
import { decrypt, encrypt } from "../../../utils/Encrypt";
import DatePicker from "react-datepicker";
import { format, set } from "date-fns";
import moment from "moment";
import { useLocation, useNavigate } from "react-router";
import { hasPermission } from "../../../utils/myFunctions";

const Session = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState("Add");
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const enc = JSON.parse(localStorage.getItem("userDetail"));
  const user = decrypt(enc?.data || []);
  const isView = hasPermission(user, location?.pathname, "isView");
  const isUpdate = hasPermission(user, location?.pathname, "isUpdate");
  const isDelete = hasPermission(user, location?.pathname, "isDelete");
  const isCreate = hasPermission(user, location?.pathname, "isCreate");
  const [timer, setTimer] = useState(5); // 5 seconds countdown
  useEffect(() => {
    if (!isView) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      // redirect to home page when the timer reaches 0
      if (timer === 0) {
        navigate(-1);
      }
      // clean up the interval when the component unmounts
      return () => clearInterval(interval);
    } else {
      setIsLoading(false);
    }
  }, [timer]);
  const logout = useLogout();
  // Calculate 2 years prior to the current year
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 5);

  // Calculate the end date as the last day of the previous month
  const lastDayOfPreviousMonth = new Date();
  lastDayOfPreviousMonth.setDate(0); // Set day to 0 to get the last day of the previous month

  // Set the date to the first day of the previous month
  const firstDayOfPreviousMonth = new Date(lastDayOfPreviousMonth);
  firstDayOfPreviousMonth.setDate(1);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    setErrors,
    clearErrors,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      status: null,
      startDate: null,
      endDate: null,
    },
  });
  const { startDate, endDate } = watch();
  const [data, setData] = useState([]);
  const [column, setColumn] = useState([
    {
      name: "Start Date",
      selector: (row) => row?.start_date,
      sortable: true,
    },
    {
      name: "End Date",
      selector: (row) => row?.end_date,
      sortable: true,
    },

    {
      name: "Status",
      selector: (row) => row?.status,
      cell: (row) => (
        <div style={{ cursor: "pointer" }} onClick={() => handleToggleStatus(row)}>
          <Badge color={`${row.status == "active" ? "success" : "danger"}`}>
            {row.status == "active" ? "Active" : "Inactive"}
          </Badge>
        </div>
      ),
      sortable: true,
      wrap: true,
    },
    {
      name: "Action",
      wrap: true,
      cell: (row) => (
        <div>
          {isUpdate && (
            <Button outline color={`warning`} size={`xs`} className={`me-1`} onClick={() => handleEdit(row)}>
              {" "}
              <Icon name="edit" />
            </Button>
          )}
        </div>
      ),
    },
  ]);
  const handleToggleStatus = (row) => {
    setSelectedRow(row);
    setShowModal(true);
  };
  useEffect(() => {
    getSession();
  }, []);
  const handleEdit = (row) => {
    window.scrollTo(0, 0);
    setIsOpen(!isOpen);
    setMode("Edit");
    setValue("id", row.id); // Add this for consistent `formData` structure
    setValue("month", {
      label: row?.month,
      value: row?.month,
    });
    trigger("subject");
    // setValue("subject_code", row?.subject_code);
    setValue("year", row.year);
    // setValue("order", row.order);
    setValue("status", {
      label: row.status == "active" ? "Active" : "Inactive",
      value: row.status,
    });
    setValue("startDate", row.start_date ? new Date(moment(row.start_date, "MMM-YYYY")) : null);
    setValue("endDate", row.end_date ? new Date(moment(row.end_date, "MMM-YYYY")) : null);
  };

  const getSession = () => {
    setIsLoading(true);
    const data = {};
    getSessionAPI(data)
      .then((res) => {
        // decrypt data
        const status = res.data.status;
        // const status = decrypt(res.data.status);
        if (status === true) {
          // const decryptedResponse = decrypt(res.data.data.tableData);
          setData(res.data.data);
          setIsLoading(false);
          toast.clearWaitingQueue();
        } else if (status === false) {
          setIsLoading(false);
          toast.warning(res.data.message);
          toast.clearWaitingQueue();
          setData([]);
        } else if (status === "expired") {
          toast.error(res.data.message);
          logout();
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };
  const onFormSubmit = (data) => {
    const body = {
      year: format(watch("startDate"), "yyyy"),
      month: format(watch("startDate"), "MMMM"),
      start_date: watch("startDate") ? format(watch("startDate"), "MMMM-yyyy") : null,
      end_date: watch("endDate") ? format(watch("endDate"), "MMMM-yyyy") : null,
      status: watch("status")?.value,
      ...(mode == "Edit" && { id: watch("id") }),
    };
    if (mode == "Add") {
      createSessionAPI(body)
        .then((res) => {
          // const status = decrypt(res.data.status);
          const status = res.data.status;
          if (status === true) {
            toast.success(res.data.message);
            toast.clearWaitingQueue();
            getSession();
            setIsOpen(false);
            setIsLoading(false);
          } else if (status === false) {
            setIsLoading(false);
            toast.error(res.data.message);
            toast.clearWaitingQueue();
          } else if (status === "expired") {
            setIsLoading(false);
            toast.error(res.data.message);
            toast.clearWaitingQueue();
            logout();
          }
        })
        .catch((err) => {
          console.log("cat", err);

          setIsLoading(false);
          toast.error(err.message);
        });
    }
    if (mode == "Edit") {
      updateSessionAPI(body)
        .then((res) => {
          // const status = decrypt(res.data.status);
          const status = res.data.status;
          if (status === true) {
            toast.success(res.data.message);
            toast.clearWaitingQueue();
            getSession();
            setIsOpen(false);
            setIsLoading(false);
          } else if (status === false) {
            setIsLoading(false);
            toast.error(res.data.message);
            toast.clearWaitingQueue();
          } else if (status === "expired") {
            setIsLoading(false);
            toast.error(res.data.message);
            toast.clearWaitingQueue();
            logout();
          }
        })
        .catch((err) => {
          setIsLoading(false);
          toast.error(err.message);
        });
    }
  };
  const handleMonthChange = (sel) => {
    setValue("month", sel);
    trigger("month");
  };
  const confirmStatusChange = () => {
    const updatedStatus = selectedRow.status === "active" ? "inactive" : "active";
    const formObject = {
      id: selectedRow.id,
      status: updatedStatus,
    };

    const encryptedData = encrypt(formObject);
    const encryptData = {
      zero: encryptedData,
    };
    updateSessionAPI(formObject)
      .then((res) => {
        // decrypt data
        // const status = decrypt(res.data.status);
        const status = res.data.status;
        if (status === true) {
          getSession();

          setShowModal(false);
          toast.success(res.data.message);
          toast.clearWaitingQueue();
        } else if (status === false) {
          toast.error(res.data.message);
          toast.clearWaitingQueue();
        } else if (status === "expired") {
          toast.error(res.data.message);
          toast.clearWaitingQueue();
          logout();
        }
      })
      .catch((err) => console.error("Error updating status:", err));
  };
  var formattedStartDate;
  var formattedEndDate;
  const handleUploadDateChange = (newStartDate) => {
    setValue("startDate", newStartDate);

    if (newStartDate != null) {
      formattedStartDate = format(newStartDate, "yyyy-MM-dd");
    }
  };
  const handleUploadDateChange1 = (newStartDate) => {
    setValue("endDate", newStartDate, { shouldValidate: true });

    if (newStartDate) {
      const formattedEndDate = format(newStartDate, "yyyy-MM-dd");
      // आप formattedEndDate को state या form में भी save कर सकते हैं
      console.log("Formatted End Date:", formattedEndDate);
    } else {
      // अगर date null है तो error सेट करें
      setErrors("endDate", {
        type: "required",
        message: "End Date is required",
      });
    }
  };

  console.log("Seeeee", errors);

  return (
    <>
      <Head title="Session | Master" />
      <Content>
        {!isView ? (
          <div className="p-5" style={{}}>
            <p style={{ color: "crimson", textAlign: "center" }}>{"Unauthorized"}</p>
            <p style={{ textAlign: "center" }}>
              You will be redirected to the home page in <b style={{ color: "crimson" }}>{timer} </b> seconds.
            </p>
          </div>
        ) : (
          <>
            <BlockHead size={`sm`}>
              <BlockBetween>
                <BlockHeadContent>
                  <BlockTitle page tag="h3">
                    {"Session"}
                  </BlockTitle>
                </BlockHeadContent>
                {isCreate && (
                  <BlockHeadContent>
                    <div className="">
                      <Button
                        color="primary"
                        outline
                        className={`  me-n1`}
                        onClick={() => {
                          setIsOpen(!isOpen);
                          setMode("Add");
                          reset();
                        }}
                      >
                        {<Icon name={isOpen ? "minus" : "plus"} />}
                      </Button>
                    </div>
                  </BlockHeadContent>
                )}
              </BlockBetween>
            </BlockHead>

            <Block>
              <Row className={`g-gs`}>
                {isOpen && (
                  <PreviewAltCard>
                    <Form onSubmit={handleSubmit(onFormSubmit)}>
                      <Row className={`g-gs`}>
                        {/* Month */}
                        {/* <Col md="4" sm="3" xxl="3">
                      <div className="form-group">
                        <Label className="from-label" htmlFor="month">
                          Month
                          <Required />
                        </Label>
                        <div className="form-control-wrap">
                          <Select
                            className=""
                            id="month"
                            options={monthList}
                            {...register("month", {
                              required: "Please select month",
                            })}
                            onChange={handleMonthChange}
                            value={watch("month")}
                          />
                          {errors.month && <span className="invalid">{errors?.month?.message}</span>}
                        </div>
                      </div>
                    </Col> */}
                        {/*Year */}
                        {/* <Col md="4" sm="3" xxl="3">
                      <div className="form-group">
                        <Label className="form-label" htmlFor="year">
                          {"Year"}
                          <Required />
                        </Label>
                        <div className="form-control-wrap">
                          <input
                            placeholder={"Enter session year"}
                            type="text"
                            id="year"
                            {...register("year", {
                              required: {
                                value: true,
                                message: "Session year is required",
                              },
                              minLength: {
                                value: 4,
                                message: "Session year must be at least 4 characters long",
                              },
                              maxLength: {
                                value: 4,
                                message: "Session year cannot exceed 4 characters",
                              },
                            })}
                            className="form-control"
                            value={watch("year")}
                            onChange={(e) => {
                              // let value = e.target.value.replace(/[^A-Za-z\s]/g, ""); // Remove invalid characters
                              // value = value.replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
                              setValue("year", e.target.value);
                              trigger("year"); // Trigger validation
                            }}
                            // onInput={(e) => {
                            //   e.target.value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                            // }}
                          />
                          {errors.year && <span className="invalid">{errors?.year?.message}</span>}
                        </div>
                      </div>
                    </Col>{" "} */}

                        <Col md="4" sm="3" xxl="3">
                          <div className="form-group">
                            <label className="form-label">
                              {"Start Date"}
                              <Required />
                            </label>

                            <Controller
                              name="startDate"
                              control={control}
                              rules={{
                                required: "Start Date is required",
                              }}
                              render={({ field }) => (
                                <DatePicker
                                  {...field}
                                  selected={field.value}
                                  onChange={(date) => field.onChange(date)}
                                  dateFormat="MMMM-yyyy"
                                  className="form-control map-overlap"
                                  minDate={twoYearsAgo}
                                  placeholderText="select period"
                                  showMonthYearPicker
                                  onKeyDown={(e) => e.preventDefault()}
                                />
                              )}
                            />

                            {errors.startDate && <span className="invalid">{errors.startDate.message}</span>}
                          </div>
                        </Col>
                        <Col md="4" sm="3" xxl="3">
                          <div className="form-group">
                            <label className="form-label">
                              {"End Date"}
                              <Required />
                            </label>
                            <Controller
                              name="endDate"
                              control={control}
                              rules={{
                                required: "Start Date is required",
                              }}
                              render={({ field }) => (
                                <DatePicker
                                  {...field}
                                  selected={field.value}
                                  onChange={(date) => field.onChange(date)}
                                  dateFormat="MMMM-yyyy"
                                  className="form-control map-overlap"
                                  minDate={twoYearsAgo}
                                  placeholderText="select period"
                                  showMonthYearPicker
                                  onKeyDown={(e) => e.preventDefault()}
                                />
                              )}
                            />
                            {errors.endDate && <span className="invalid">{errors.endDate.message}</span>}
                          </div>
                        </Col>
                        {/* Status */}
                        <Col md="2" sm="3" xxl="2">
                          <Status
                            setValue={setValue}
                            trigger={trigger}
                            register={register}
                            watch={watch}
                            errors={errors}
                          />
                        </Col>
                      </Row>
                      <Col md="2" className={``}>
                        <MyButton isLoading={isLoading} mode={mode} />
                      </Col>
                    </Form>
                  </PreviewAltCard>
                )}
                <MyDataTable data={data} columns={column} title={"Subject"} isLoading={isLoading} />
              </Row>
            </Block>
          </>
        )}
      </Content>
      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showModal}
        toggle={() => setShowModal(!showModal)}
        onConfirm={confirmStatusChange}
        status={selectedRow?.status}
      />
    </>
  );
};

export default Session;
